<template>
    <b-modal id="createModal" body-class="px-0" ref="modal" title="Create a commercial variant" v-model="show" @ok="handleSubmit" @hide="close"
             @shown="focusNameInput">

        <div class="card card-flat mb-0">
            <div class="bg-light">
                <h6 class="card-header text-secondary">Base Details</h6>
            </div>
            <div class="card-body">
                <div class="form-group">
                    <label>Commercial variant name</label>

                    <div class="position-relative">
                        <input type="text" ref="nameInput" name="name" v-validate="{required: true}" class="form-control" v-model="name">
                        <div class="invalid-feedback" v-if="errors.has('name')">{{ errors.first('name') }}</div>
                        <i class="form-group__bar"></i>
                    </div>
                </div>

                <div class="form-group">
                    <label>HSR number</label>
                    <div class="position-relative">
                        <input type="text" name="hsr_number" v-validate="{required: true}" class="form-control" v-model="hsr_number">
                        <div class="invalid-feedback" v-if="errors.has('hsr_number')">{{ errors.first('hsr_number') }}</div>
                        <i class="form-group__bar"></i>
                    </div>
                </div>

                <div class="form-group">
                    <label>UN number</label>
                    <div class="position-relative">
                        <input type="text" name="un_number" v-validate="{required: true}" class="form-control" v-model="un_number">
                        <div class="invalid-feedback" v-if="errors.has('un_number')">{{ errors.first('un_number') }}</div>
                        <i class="form-group__bar"></i>
                    </div>
                </div>

                <div class="form-group">
                    <label>Chemical State</label>
                    <div>
                        <select v-model="is_liquid" class="form-control">
                            <option :value="true">Liquid</option>
                            <option :value="false">Solid</option>
                        </select>
                    </div>
                </div>

                <div class="form-group">
                    <label>Classification</label>
                    <div class="position-relative">
                        <VueSelect
                                data-vv-as="classification"
                                label="name"
                                track-by="id"
                                v-validate="validation.classification"
                                v-model="classification"
                                data-vv-name="classification"
                                :options="classifications"
                                :allow-empty="false">
                        </VueSelect>
                        <div class="invalid-feedback" v-if="errors.has('classification')">{{ errors.first('classification') }}</div>
                        <i class="form-group__bar"></i>
                    </div>
                </div>
            </div>
        </div>
        <div class="card card-flat mb-0">
            <div class="bg-light">
                <h6 class="card-header text-secondary" v-if="cropTypeId">Crop Type</h6>
                <h6 class="card-header text-secondary" v-else>Crop Types</h6>
            </div>
            <div class="card-body">
                <div class="form-group" v-if="!cropTypeId">
                    <b-table hover small class="no-borders" stacked="md" :items="cropTypeEntryRows" :fields="cropTypeEntriesTable.fields"
                             :show-empty="true">
                        <template slot="cropType" slot-scope="row">
                            <div class="position-relative">
                                <VueSelect
                                        data-vv-as="cropType"
                                        placeholder="CropType"
                                        label="name"
                                        track-by="id"
                                        v-b-tooltip="errors.first(`cropType_${row.index}`) || ''"
                                        v-validate="validation.entry_cropType"
                                        :value="row.item.cropType"
                                        :data-vv-name="`cropType_${row.index}`"
                                        :options="cropTypes"
                                        :allow-empty="false"
                                        :show-labels="false"
                                        @input="setCropTypeRow('cropType', row.index, $event)">
                                    <div slot="noResult" class="text-center">
                                        <small class="text-muted">No matches</small>
                                    </div>
                                </VueSelect>
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="is_usable" slot-scope="row">
                            <div class="position-relative">
                                <div class="custom-control custom-checkbox">

                                    <input class="custom-control-input"
                                           type="checkbox"
                                           v-model="row.item.is_usable"
                                    >
                                    <label class="custom-control-label">&nbsp;</label>
                                </div>
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="is_registered" slot-scope="row">
                            <div class="position-relative">
                                <div class="custom-control custom-checkbox">

                                    <input class="custom-control-input"
                                           type="checkbox"
                                           v-model="row.item.is_registered"
                                    >
                                    <label class="custom-control-label">&nbsp;</label>
                                </div>
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="recommended_rate" slot-scope="row">
                            <div class="position-relative">
                                <VueMask
                                        class="form-control"
                                        placeholder="0..."
                                        v-b-tooltip="errors.first(`recommended_rate_${row.index}`) || ''"
                                        :value="row.item.recommended_rate"
                                        :class="{'is-invalid': errors.has(`recommended_rate_${row.index}`)}"
                                        :data-vv-name="`recommended_rate_${row.index}`"
                                        :options="{numeral: true, min:0, max:100, numeralThousandsGroupStyle: 'thousand'}"
                                        @input="setCropTypeRow('recommended_rate', row.index, $event)" required />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>

                        <template slot="remove" slot-scope="row">
                            <b-button size="sm" variant="outline-danger" @click="removeCropTypeRow(row.index)">Remove</b-button>
                        </template>
                    </b-table>

                    <div class="mx-3">
                        <button type="button" class="btn btn-block btn-outline-success" @click.prevent="addCropTypeRow">
                            <i class="zmdi zmdi-plus"></i> Add another row
                        </button>
                    </div>
                </div>
                <template v-else>
                    <div class="form-group">
                        <label>Registered For Use</label>
                        <div>
                            <select v-model="crop_is_registered" class="form-control">
                                <option :value="true">Registered</option>
                                <option :value="false">Off Label</option>
                            </select>
                        </div>
                    </div>

                    <div class="form-group">
                        <label>Recommended Rate</label>
                        <div class="position-relative">
                            <VueMask
                                    class="form-control"
                                    placeholder="0..."
                                    v-model="crop_recommended_rate"
                                    data-vv-name="crop_recommended_rate"
                                    data-vv-as="Recommended rate"
                                    v-validate="validation.crop_recommended_rate"
                                    :class="{'is-invalid': errors.has(`crop_recommended_rate`)}"
                                    :options="{numeral: true, min:0, max:1000, numeralThousandsGroupStyle: 'thousand'}" />
                            <div class="invalid-feedback" v-if="errors.has('crop_recommended_rate')">{{ errors.first('crop_recommended_rate') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>
                </template>

            </div>
        </div>

        <div class="card card-flat mb-0">
            <div class="bg-light">
                <h6 class="card-header text-secondary">Active Ingredients</h6>
            </div>
            <div class="card-body">
                <div class="form-group">
                    <b-table hover small class="no-borders" stacked="md" :items="activeIngredientEntryRows"
                             :fields="activeIngredientEntriesTable.fields"
                             :show-empty="true">
                        <template slot="chemical" slot-scope="row">
                            <div class="position-relative">
                                <VueSelect
                                        data-vv-as="chemical"
                                        placeholder="Chemical"
                                        label="name"
                                        track-by="id"
                                        v-b-tooltip="errors.first(`chemical_${row.index}`) || ''"
                                        v-validate="validation.entry_chemical"
                                        :value="row.item.chemical"
                                        :data-vv-name="`chemical_${row.index}`"
                                        :options="activeIngredients"
                                        :allow-empty="false"
                                        :show-labels="false"
                                        @input="setActiveIngredientRow('chemical', row.index, $event)">
                                    <div slot="noResult" class="text-center">
                                        <small class="text-muted">No matches</small>
                                    </div>
                                </VueSelect>
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="ingredient_percentage" slot-scope="row">
                            <div class="position-relative">
                                <VueMask
                                        class="form-control"
                                        placeholder="0..."
                                        v-validate="validation.ingredient_percentage"
                                        v-b-tooltip="errors.first(`ingredient_percentage_${row.index}`) || ''"
                                        data-vv-as="ingredient percentage"
                                        :value="row.item.ingredient_percentage"
                                        :class="{'is-invalid': errors.has(`ingredient_percentage_${row.index}`)}"
                                        :data-vv-name="`ingredient_percentage_${row.index}`"
                                        :options="{numeral: true, min:0, max:100, numeralThousandsGroupStyle: 'thousand'}"
                                        @input="setActiveIngredientRow('ingredient_percentage', row.index, $event)" required />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="remove" slot-scope="row">
                            <b-button size="sm" variant="outline-danger" @click="removeActiveIngredientRow(row.index)">Remove</b-button>
                        </template>
                    </b-table>
                    <div class="mx-3">
                        <button type="button" class="btn btn-block btn-outline-success" @click.prevent="addActiveIngredientRow">
                            <i class="zmdi zmdi-plus"></i> Add another row
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <template slot="modal-footer">
            <button class="btn btn-secondary" @click="close">
                Cancel
            </button>
            <button class="btn btn-primary" :disabled="okDisabled" @click="handleSubmit">
                Save
            </button>
        </template>
    </b-modal>
</template>


<script>
import listCommercialVariantQuery from '@views/ChemicalCommercialVariants/Browse/queryListChemicalCommercialVariants.graphql';
import listCommercialVariantNonGroupedQuery from '@views/ChemicalCommercialVariants/Browse/queryListChemicalCommercialVariants.graphql';
import createCommercialVariantMutation from '@views/ChemicalCommercialVariants/CreateModal/mutationCreateChemicalCommercialVariant.graphql';
import listActiveIngredientsQuery from '@views/ChemicalCommercialVariants/EditModal/queryListChemicalActiveIngredients.graphql';
import listCropTypesQuery from '@views/ChemicalCommercialVariants/EditModal/queryListCropTypes.graphql';

export default {
  name: 'create-modal',
  apollo: {
    activeIngredients: {
      query: listActiveIngredientsQuery,
      update: data => data.filterChemicalActiveIngredients.data,
      variables: { input: { include_deleted: true } },
    },
    cropTypes: {
      query: listCropTypesQuery,
      update: data => data.filterCropTypes.data,
      variables: { input: { include_deleted: true } },
    },
  },
  props: {
    close: {
      type: Function,
      required: true,
    },
    classifications: {
      type: Array,
      required: true,
    },
    cropTypeId: {},
  },
  data() {
    return {
      name: '',
      is_liquid: false,
      activeIngredients: [],
      crop_is_registered: false,
      crop_recommended_rate: null,
      hsr_number: null,
      un_number: null,
      cropTypes: [],
      classification: null,
      cropTypeEntryRows: [{
        cropType: null,
        is_usable: false,
        is_registered: false,
        recommended_rate: null,
      }],
      activeIngredientEntryRows: [{
        chemical: null,
        ingredient_percentage: null,
      }],
      show: true,
      loading: 0,
      validation: {
        entry_chemical: { required: true },
        classification: { required: true },
        ingredient_percentage: { required: true, min_value: 0 },
      },
      activeIngredientEntriesTable: {
        fields: [
          { key: 'chemical', label: 'Chemical' },
          { key: 'ingredient_percentage', label: 'Ingredient percentage' },
          { key: 'remove', label: 'Remove', 'class': 'text-right' },
        ],
      },
      cropTypeEntriesTable: {
        fields: [
          { key: 'cropType', label: 'CropType' },
          { key: 'is_usable', label: 'Is Usable' },
          { key: 'is_registered', label: 'Is Registered' },
          { key: 'recommended_rate', label: 'Recommended Rate' },
          { key: 'remove', label: 'Remove', 'class': 'text-right' },
        ],
      },
    };
  },
  computed: {
    okDisabled() {
      return this.name.length === 0;
    },
  },
  methods: {
    addActiveIngredientRow() {
      this.$validator.validateAll().then(validationWasSuccessful => {
        if (validationWasSuccessful) {
          this.activeIngredientEntryRows.push({
            chemical: null,
            ingredient_percentage: null,
          });
        }
      });
    },
    addCropTypeRow() {
      this.$validator.validateAll().then(validationWasSuccessful => {
        if (validationWasSuccessful) {
          this.cropTypeEntryRows.push({
            cropType: null,
            is_usable: false,
            is_registered: false,
            recommended_rate: null,
          });
        }
      });
    },
    removeCropTypeRow(index) {
      this.cropTypeEntryRows.splice(index, 1);
    },
    removeActiveIngredientRow(index) {
      this.activeIngredientEntryRows.splice(index, 1);
    },
    setCropTypeRow(field, index, value) {
      this.cropTypeEntryRows[index][field] = value;
    },
    setActiveIngredientRow(field, index, value) {
      this.activeIngredientEntryRows[index][field] = value;
    },
    handleSubmit() {
      if (this.okDisabled) {
        return false;
      }
      this.errors.remove('global');
      this.$validator.validateAll().then(validationWasSuccessful => {
        if (!validationWasSuccessful) {
          return;
        }

        const input = {
          classification_id: this.classification.id,
          name: this.name,
          hsr_number: this.hsr_number,
          un_number: this.un_number,
          is_liquid: this.is_liquid,
          active_ingredients: this.activeIngredientEntryRows.map(row => ({
            id: row.chemical.id,
            active_ingredient_percentage: row.ingredient_percentage,
          })),
        };

        if (!this.cropTypeId) {
          input.crop_types = this.cropTypeEntryRows.map(row => ({
            id: row.cropType.id,
            is_usable: row.is_usable,
            is_registered: row.is_registered,
            recommended_rate: row.recommended_rate,
          }));
        } else {
          input.crop_recommended_rate = this.crop_recommended_rate;
          input.crop_is_registered = this.crop_is_registered;
        }

        this.$apollo
          .mutate({
            mutation: createCommercialVariantMutation,
            refetchQueries: [
              { query: listCommercialVariantQuery, variables: { input: { classification_id: this.classification.id } } },
              { query: listCommercialVariantQuery, variables: { input: { classification_id: this.classification.id, include_deleted: true } } },
              { query: listCommercialVariantNonGroupedQuery },
              { query: listCommercialVariantNonGroupedQuery, variables: { input: { include_deleted: true } } },
            ],
            variables: {
              input,
            },
          })
          .then(() => {
            this.$notifySuccess('Commercial variant created!');
            this.close();
          })
          .catch(err => {
            this.$mapGraphQLError(err);
          })
          .catch(err => {
            console.error(err);
            this.$notifyFailure(
              'Unfortunately, we were unable to do that!',
            );
          });
      });
    },
    focusNameInput() {
      this.$refs.nameInput.focus();
    },
  },
};
</script>
